import { HttpClient } from '@angular/common/http';
import { inject, Type } from '@angular/core';
import { Location } from '@angular/common';
import { map, Observable } from 'rxjs';
import { ApiResource } from './api-resource';
import { environment } from '../../environments/environment';
import 'reflect-metadata';
import { ApiResponse } from './api.interfaces';
import {
  ApiQueryParams,
  Filter,
  FilterOperator,
  HttpOptions,
  isQueryOperator,
  isQuerySuffix,
  mergeDefaultParams,
} from './api.operators';

export const API_META = Symbol('API response metadata');

function toFilterValue(value: unknown): unknown {
  if (value instanceof Date) {
    return value.toISOString();
  } else if (value === null) {
    // Null values can only be serialized as the null operator.
    return FilterOperator.NULL;
  } else {
    return value;
  }
}

export abstract class BeehiveApiService<R extends ApiResource<R>> {
  protected abstract readonly entityType: Type<R>;
  protected abstract readonly endpoint: string;
  protected readonly http = inject(HttpClient);

  protected getUrl(...path: string[]): string {
    return path.reduce(
      Location.joinWithSlash,
      Location.joinWithSlash(environment.backendUri, this.endpoint),
    );
  }

  public getEntityType() {
    return this.entityType;
  }

  public create(
    data: Partial<R>,
    rawParams?: ApiQueryParams<R>,
  ): Observable<R> {
    const url = this.getUrl();
    const params = this.parseQuery(rawParams);
    return this.http
      .post<ApiResponse<R>>(url, { data: sanitizeDeeper(data) }, params)
      .pipe(
        map((response) => castDataToEntity(this.entityType, response.data)),
      );
  }

  public read(id: string, rawParams?: ApiQueryParams<R>): Observable<R> {
    const url = this.getUrl(encodeURIComponent(id));
    const params = this.parseQuery(rawParams);
    return this.http
      .get<ApiResponse<R>>(url, params)
      .pipe(
        map((response) => castDataToEntity(this.entityType, response.data)),
      );
  }

  public update(
    id: string,
    data: any,
    rawParams?: ApiQueryParams<R>,
  ): Observable<R> {
    const url = this.getUrl(encodeURIComponent(id));
    const params = this.parseQuery(rawParams);

    return this.http
      .patch<ApiResponse<R>>(url, { data: sanitizeDeeper(data) }, params)
      .pipe(
        map((response) => castDataToEntity(this.entityType, response.data)),
      );
  }

  public delete(id: string, rawParams?: ApiQueryParams<R>): Observable<void> {
    const url = this.getUrl(encodeURIComponent(id));
    const params = this.parseQuery(rawParams);
    return this.http.delete<void>(url, params);
  }

  public list(params?: ApiQueryParams<R>): Observable<R[]> {
    return this.getMany([], mergeDefaultParams(params), this.entityType);
  }

  public listRelated<T extends ApiResource<any> = ApiResource<unknown>>(
    id: string,
    endpoint: string,
    relatedType: Type<T>,
    params?: ApiQueryParams<T>,
  ): Observable<T[]> {
    return this.getMany(
      [id, endpoint],
      mergeDefaultParams(params),
      relatedType,
    );
  }

  attach(
    id: string,
    endpoint: string,
    relatedKey: string,
    child: { id: string },
  ): Observable<R> {
    return this.post([id, endpoint], { [relatedKey]: child });
  }

  detach(id: string, endpoint: string, child: { id: string }) {
    const url = this.getUrl(id, endpoint, child.id);
    return this.http.delete(url);
  }

  public count(
    rawParams?: ApiQueryParams<R>,
    path: string[] = [],
  ): Observable<number> {
    const url = this.getUrl(...path);
    const params = this.parseQuery(rawParams);
    ((params.params ??= {}) as any)['limit'] = 0;
    return this.http
      .get<ApiResponse<R[]>>(url, params)
      .pipe(map((response) => response.meta.totalItems));
  }

  protected getMany<E extends ApiResource<any> = R>(
    path: string[],
    params: ApiQueryParams<E> | undefined,
    type: Type<E> | 'primitive',
  ): Observable<E[]> {
    const url = this.getUrl(...path);
    const options = this.parseQuery(params);
    return this.http.get<ApiResponse<E[]>>(url, options).pipe(
      map((response) => {
        const data =
          type === 'primitive'
            ? response.data
            : castDataArrayToEntityArray(type, response.data);
        Reflect.defineMetadata(API_META, response.meta, data);
        return data;
      }),
    );
  }

  protected findOne<E extends ApiResource<any> = R>(
    path: string[],
    params: ApiQueryParams<E> | undefined,
    type: Type<E>,
  ): Observable<E | null> {
    return this.getMany([], mergeDefaultParams(params), type).pipe(
      map((resource) => {
        if (resource.length > 1)
          throw new Error(
            `Found multiple (${resource.length}) results for a find one query.`,
          );
        return resource[0] ?? null;
      }),
    );
  }

  protected post<D, E>(
    path: string[],
    data: D,
    params?: Record<string, any>,
  ): Observable<E> {
    const url = this.getUrl(...path);
    return this.http
      .post<ApiResponse<E>>(url, { data: sanitizeDeeper(data) }, { params })
      .pipe(map((response) => response.data as E));
  }

  /**
   * Parse the given query and HTTP options into an Angular HTTP options object
   * @param query
   * @param options
   * @protected
   */
  protected parseQuery<T extends ApiResource<T>>(
    query: ApiQueryParams<T> = {},
    options: HttpOptions = {},
  ): HttpOptions {
    const params: (typeof options)['params'] = { ...options.params };
    options.params = params;
    this.setFilters(query, params);
    this.setSearch(query, params);
    this.setPage(query, params);
    this.setSorting(query, params);
    return options;
  }

  private setSorting<T extends ApiResource<T>>(
    query: ApiQueryParams<T>,
    params: any,
  ) {
    if (query.sorts) {
      params['sortBy'] = query.sorts.map((pair) => pair.join(':'));
    }
  }

  private setFilters<T extends ApiResource<T>>(
    query: ApiQueryParams<T>,
    params: any,
  ) {
    for (const row of Object.entries(query.filters || {})) {
      let [field, filter]: [string, any] = row;
      if (Array.isArray(filter) && this.isMultipleFilters(filter)) {
        filter.forEach((subfilter) =>
          this.setFilters({ filters: { [field]: subfilter } }, params),
        );
        continue;
      } else if (
        !(
          Array.isArray(filter) &&
          (isQueryOperator(filter[0]) || isQuerySuffix(filter[0]))
        )
      ) {
        // Transform shorthand values (those that do not conform to [operator, value(s)])
        filter =
          // Replace null by the null operator
          filter === null
            ? [FilterOperator.NULL]
            : [
                // If an array of values is given, use the in operator, otherwise the eq operator.
                Array.isArray(filter) ? FilterOperator.IN : FilterOperator.EQ,
                filter,
              ];
      }
      // Transform dates etc. to their filter value. (Also copies array to prevent mutation of given filters)
      filter = filter.map(toFilterValue);
      // For $in and $btw, join values with comma's
      if (Array.isArray(filter[filter.length - 1])) {
        filter[filter.length - 1] = filter[filter.length - 1].join(',');
      }
      params[`filter.${field}`] = [
        ...(params[`filter.${field}`] ?? []),
        filter.join(':'),
      ];
    }
  }

  private isMultipleFilters(filters: Filter[]) {
    return (
      Array.isArray(filters) && filters.every((filter) => Array.isArray(filter))
    );
  }

  private setSearch<T extends ApiResource<T>>(
    query: ApiQueryParams<T>,
    params: any,
  ) {
    if (query.search !== undefined) params['search'] = query.search;
  }

  private setPage<T extends ApiResource<T>>(
    query: ApiQueryParams<T>,
    params: any,
  ) {
    if (query.page !== undefined) {
      params['limit'] = query.page.limit;
      params['page'] = query.page.page;
    }
  }
}

export function sanitizeDeeper(data: any): any {
  const getValue = (value: any) =>
    value !== null && typeof value === 'object' && 'id' in value
      ? { id: value.id }
      : value;

  if (Array.isArray(data)) {
    return data.map(getValue);
  }

  return Object.fromEntries(
    Object.entries(data).map(([key, value]) => [
      key,
      Array.isArray(value) ? value.map(getValue) : getValue(value),
    ]),
  );
}

// todo: make this recursive
export function castDataToEntity<T extends ApiResource<T>>(
  entityType: Type<T>,
  data: any,
): T {
  return new entityType(data);
}

// todo: make this recursive
export function castDataArrayToEntityArray<T extends ApiResource<T>>(
  entityType: Type<T>,
  data: any[],
): T[] {
  return data.map((item) => castDataToEntity(entityType, item));
}
